import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useCentralizedAuth } from '@/context/CentralizedAuthProvider';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useBookingPayment } from '@/hooks/useBookingPayment';
import { usePartners } from '@/hooks/usePartners';
import { PartnerWithDetails } from '@/types/partner';
import RazorpayScript from '@/components/payment/RazorpayScript';
import { getFromSessionStorage } from '@/utils/storage/sessionStorageService';
import { CareerCounselor } from '@/hooks/useCareerCounselors';
import { supabase } from '@/integrations/supabase/client';

const BookSession = () => {
  const { isAuthenticated, user } = useCentralizedAuth();
  const { profile } = useUserProfile(user?.id);
  const navigate = useNavigate();
  const location = useLocation();
  const { getPartnerById, loading: partnerLoading } = usePartners();
  
  const [partner, setPartner] = useState<PartnerWithDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(false);
  const [finalAmount, setFinalAmount] = useState(0);
  
  // Get query parameters
  const queryParams = new URLSearchParams(location.search);
  const partnerId = queryParams.get('partnerId') || '';
  const associationId = queryParams.get('associationId') || '';
  const bookingId = queryParams.get('bookingId') || '';
  const storageKey = queryParams.get('storageKey') || '';
  
  // Get booking details
  const [bookingDetails, setBookingDetails] = useState<{
    id: string;
    amount: number;
    sessionTime?: Date;
    duration?: number;
  } | null>(null);
  
  const { isProcessing, processPayment } = useBookingPayment({
    userId: user?.id,
    userProfile: profile,
    bookingId: bookingDetails?.id || '',
    finalAmount
  });

  // Function to get counselor data from storage
  const getCounselorFromStorage = (): CareerCounselor | null => {
    if (!storageKey && !partnerId) return null;
    
    try {
      if (storageKey) {
        return getFromSessionStorage<CareerCounselor>(storageKey);
      }
      
      if (partnerId) {
        const fallbackKey = `counselor_${partnerId}`;
        return getFromSessionStorage<CareerCounselor>(fallbackKey);
      }
      
      return null;
    } catch (error) {
      console.error('Error retrieving counselor from sessionStorage:', error);
      return null;
    }
  };

  // Fetch booking details from the database
  const fetchBookingDetails = async (bookingId: string) => {
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select('*')
        .eq('id', bookingId)
        .single();
      
      if (error) {
        console.error('Error fetching booking:', error);
        throw error;
      }
      
      if (!data) {
        throw new Error('Booking not found');
      }
      
      return data;
    } catch (error) {
      console.error('Error in fetchBookingDetails:', error);
      throw error;
    }
  };

  useEffect(() => {
    const loadBookingData = async () => {
      try {
        if (!isAuthenticated) {
          toast.error("Please log in to continue with payment");
          navigate("/login");
          return;
        }

        setIsLoading(true);
        setLoadingError(false);
        
        // If we have a bookingId, fetch the booking details
        if (bookingId) {
          try {
            // Fetch booking from database
            const bookingData = await fetchBookingDetails(bookingId);
            
            // Use the paid_amount from the database
            const bookingAmount = bookingData.paid_amount || 0;
            setFinalAmount(bookingAmount);
            
            // If we have a partner ID, fetch the partner details
            if (partnerId) {
              try {
                const partnerData = await getPartnerById(partnerId);
                if (partnerData) {
                  setPartner(partnerData);
                }
              } catch (error) {
                console.error('Error fetching partner:', error);
              }
            } else {
              // Try to get data from session storage
              const counselorData = getCounselorFromStorage();
              if (counselorData) {
                // Only use this for display, not for payment amount
              }
            }
            
            setBookingDetails({
              id: bookingId,
              amount: bookingAmount
            });
          } catch (error) {
            console.error('Error fetching booking data:', error);
            setLoadingError(true);
          }
        }
      } catch (error) {
        console.error('Error loading booking data:', error);
        setLoadingError(true);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadBookingData();
  }, [isAuthenticated, partnerId, associationId, bookingId, navigate]);

  const handleGoBack = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-lg">Loading payment details...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }
  
  if (loadingError || !bookingDetails) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-grow flex items-center justify-center">
          <Card className="w-full max-w-md">
            <CardContent className="p-6">
              <div className="text-center">
                <h2 className="text-xl font-bold text-red-600 mb-4">Payment Error</h2>
                <p className="mb-6">Unable to load payment details. Please try again.</p>
                <Button onClick={handleGoBack}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Go Back
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <RazorpayScript />
      
      <main className="flex-grow py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={handleGoBack}
            className="mb-6 text-muted-foreground flex items-center"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          
          <Card>
            <CardContent className="p-6">
              <div className="mb-6">
                <h1 className="text-2xl font-bold mb-2">Complete Your Booking</h1>
                {partner && (
                  <div className="flex items-center gap-4 mt-4">
                    <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                      {partner.profile_image ? (
                        <img src={partner.profile_image} alt={partner.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-xl font-semibold">
                          {partner.name.split(' ').map(n => n[0]).join('')}
                        </span>
                      )}
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold">{partner.name}</h2>
                      <p className="text-muted-foreground">Session Booking</p>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="bg-gray-50 p-6 rounded-lg border mb-6">
                <h3 className="font-semibold text-lg mb-4">Booking Summary</h3>
                
                <div className="space-y-2">
                  <div className="flex justify-between items-center pt-2 font-semibold">
                    <span className="text-sm">Total Amount:</span>
                    <span className="text-lg">{finalAmount} INR</span>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-center">
                <Button 
                  onClick={processPayment}
                  disabled={isProcessing}
                  className="w-full max-w-sm bg-green-600 hover:bg-green-700"
                >
                  {isProcessing ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> 
                      Processing Payment...
                    </>
                  ) : (
                    "Complete Payment"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BookSession;
